import { useState, useContext, useEffect, useMemo, Fragment } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import moment from "moment";

import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/footer";
import HelmetSEO from "../../components/helmetSeo/helmetSeo";
import HeaderBannerPanel from "../../bloks/panels/headerBannerPanel/headerBannerPanel";
import LowerBannerPanel from "../../bloks/panels/lowerBannerPanel/lowerBannerPanel";
import Radio from "../../components/radio/Radio";
import Checkbox from "../../components/checkbox/Checkbox";
import SearchCard from "../../components/searchCard/SearchCard";
import Pagination from "../../components/pagination/Pagination";
import { StickyBottomAside } from "../../components/stickyAside/stickyAside";
import { SearchSVG, TimesOffCenterSVG, TriangleDownSVG } from "../../svg";

import GetComponentsFromBloks from "../../helpers/bloksRenderer";
import { storyblokStoryUuidToUrl } from "../../helpers/URLHelpers";
import { SearchResultsTemplateStoryToObject } from "../../helpers/storyHelpers";
import useSearchCategories from "../../hooks/useSearchCategories";
import { useCurrentPageSearchParam } from "../../hooks/usePagination";
import { useStoryblokGlobalSettings } from "../../hooks/useStoryblokGlobalSettings";
import { useCrunchyrollArticleSearch } from "../../hooks/useCrunchyrollArticleSearch";
import { LanguageContext } from "../../context/languagecontext";

import styles from "./searchResultsTemplate.module.scss";
import PageContentWrapper from "../../components/PageContentWrapper";

const FILTERS = { DATE: "date", CATEGORY: "category", SORT: "sort" },
  DATE_FILTERS = {
    ALL: "all",
    DAY: "day",
    WEEK: "week",
    MONTH: "month",
    YEAR: "year",
  },
  SORT_FILTERS = { RELEVANCY: "relevancy", NEWEST: "newest", OLDEST: "oldest" };

const DATE_FILTER_DEFAULT = DATE_FILTERS.ALL,
  SORT_FILTER_DEFAULT = SORT_FILTERS.RELEVANCY;

const PER_PAGE = 10,
  FEATURED = 3;

function SearchResultsTemplate() {
  const { story } = useOutletContext();

  const pagePage = storyblokStoryUuidToUrl(story.uuid);
  const pageData = SearchResultsTemplateStoryToObject(story);
  const intl = useIntl();
  const language = useContext(LanguageContext);
  const globalSettings = useStoryblokGlobalSettings();
  const [categoryFilterOptions, categoriesLoading] =
    useSearchCategories(language);

  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation();

  const [searchTerms, setSearchTerms] = useState(""),
    [termsSearched, setTermsSearched] = useState(""),
    [currentPage, setCurrentPage] = useState(useCurrentPageSearchParam() || 1);

  const [dateFilterVisible, setDateFilterVisible] = useState(false),
    [categoryFilterVisible, setCategoryFilterVisible] = useState(false),
    [sortFilterVisible, setSortFilterVisible] = useState(false),
    [dateFilterValue, setDateFilterValue] = useState(DATE_FILTER_DEFAULT),
    [categoryFilterValues, setCategoryFilterValues] = useState([]),
    [sortFilterValue, setSortFilterValue] = useState(SORT_FILTER_DEFAULT);

  const { startFilter, endFilter } = useMemo(() => {
    const now = moment();
    switch (dateFilterValue) {
      case DATE_FILTERS.DAY:
        return {
          startFilter: moment(now).subtract(1, "day"),
          endFilter: now,
        };
      case DATE_FILTERS.WEEK:
        return {
          startFilter: moment(now).subtract(1, "week"),
          endFilter: now,
        };
      case DATE_FILTERS.MONTH:
        return {
          startFilter: moment(now).subtract(1, "month"),
          endFilter: now,
        };
      case DATE_FILTERS.YEAR:
        return {
          startFilter: now.subtract(1, "year"),
          endFilter: now,
        };
      default:
        return {};
    }
  }, [dateFilterValue]);

  const [currentArticles, totalArticles, isLoading] =
    useCrunchyrollArticleSearch({
      pageSize: PER_PAGE,
      page: currentPage,
      term: termsSearched,
      category: categoryFilterValues.join(","),
      orderBy: sortFilterValue,
      startDate: startFilter,
      endDate: endFilter,
    });

  let asideComponentList = [];
  if (story && story.content && story.content.aside) {
    asideComponentList = GetComponentsFromBloks(story.content.aside, story);
  }

  useEffect(() => {
      
      // Search Term
      const term = searchParams.get("s");
      if (term) {
        const terms = decodeURIComponent(term);
        setSearchTerms(terms);
        setTermsSearched(terms);
      }
      // Date Filter
      const date = searchParams.get("d");
      if (date) {
        setDateFilterValue(date);
      }
      // Category Filter
      const catQuery = searchParams.get("c");
      if (catQuery) {
        const catText = decodeURIComponent(catQuery);
        const catList = catText.split(",");
        if (catList && catList.length > 0) {
          setCategoryFilterValues(catList);
        }
      }
      // Sort By Filter
      const order = searchParams.get("o");
      if (order) {
        setSortFilterValue(order);
      }

      setCurrentPage(useCurrentPageSearchParam());

  }, [location.search]);

  function GetSortFilterLabelFromKey(key) {
    switch (key) {
      case SORT_FILTERS.NEWEST:
        return intl.formatMessage({ id: "page.search.filter.sort.newest" });
      case SORT_FILTERS.OLDEST:
        return intl.formatMessage({ id: "page.search.filter.sort.oldest" });
      case SORT_FILTERS.RELEVANCY:
        return intl.formatMessage({ id: "page.search.filter.sort.relevancy" });
    }
  }
  function GetDateFilterLabelFromKey(key) {
    switch (key) {
      case DATE_FILTERS.ALL:
        return intl.formatMessage({ id: "page.search.filter.date.all" });
      case DATE_FILTERS.DAY:
        return intl.formatMessage({ id: "page.search.filter.date.today" });
      case DATE_FILTERS.WEEK:
        return intl.formatMessage({ id: "page.search.filter.date.week" });
      case DATE_FILTERS.MONTH:
        return intl.formatMessage({ id: "page.search.filter.date.month" });
      case DATE_FILTERS.YEAR:
        return intl.formatMessage({ id: "page.search.filter.date.year" });
      default:
        console.warn(`INVALID DATE FILTER [${key}]`);
        return "";
    }
  }

  function addWindowQuery(prop, value) {
    if (typeof window !== "undefined") {
      searchParams.set(prop, value);
      setSearchParams(Object.fromEntries(searchParams))
    }
  }
  function removeWindowQuery(prop) {
    if (typeof window !== "undefined") {
      searchParams.delete(prop);
      setSearchParams(Object.fromEntries(searchParams))
    }
  }
  function handlePageChange(newPage) {
    if (newPage !== currentPage) {
      if (newPage === 1) {
        removeWindowQuery("page")
      } else {
        addWindowQuery("page", newPage);
      }
      window.scrollTo(0, 0);
    }
  }

  function updateSearchTerms(e) {
    setSearchTerms(e.target.value || "");
  }

  function toggleFilter(filter) {
    if (filter === FILTERS.DATE) {
      setDateFilterVisible(!dateFilterVisible);
      setCategoryFilterVisible(false);
      setSortFilterVisible(false);
    } else if (filter === FILTERS.CATEGORY) {
      setDateFilterVisible(false);
      setCategoryFilterVisible(!categoryFilterVisible);
      setSortFilterVisible(false);
    } else if (filter === FILTERS.SORT) {
      setDateFilterVisible(false);
      setCategoryFilterVisible(false);
      setSortFilterVisible(!sortFilterVisible);
    }
  }
  function updateDateFilter(filter) {
    if (filter !== dateFilterValue) {
      if (filter === DATE_FILTER_DEFAULT) {
        removeWindowQuery("d");
      } else {
        addWindowQuery("d", filter);
      }
    }
    handlePageChange(1);
  }
  function updateSortFilter(filter) {
    if (filter !== sortFilterValue) {
      if (filter === SORT_FILTER_DEFAULT) {
        removeWindowQuery("o");
      } else {
        addWindowQuery("o", filter);
      }
    }
    handlePageChange(1);
  }
  function updateCategoryFilter(filter) {
    let removed = false;
    let newValues = categoryFilterValues.filter((f) => {
      if (f === filter) {
        removed = true;
        return false;
      }
      return true;
    });
    if (!removed) {
      newValues.push(filter);
    }
    handlePageChange(1);
    if (newValues && newValues.length > 0) {
      addWindowQuery("c", newValues.join(","));
    } else [removeWindowQuery("c")];
  }
  function removeCategoryFilter(value) {
    let newValues = categoryFilterValues.filter((v) => {
      return v !== value;
    });
    setCategoryFilterValues(newValues);
    handlePageChange(1);
    if (newValues && newValues.length > 0) {
      addWindowQuery("c", newValues.join(","));
    } else [removeWindowQuery("c")];
  }

  function handleInputKeyDown(e) {
    if (e.key === "Enter") {
      handleSearch();
    }
  }

  function handleClickSearch(e) {
    handleSearch();
  }

  function handleSearch() {
    if (isLoading) {
      return;
    }

    handlePageChange(1);
    if (searchTerms) {
      addWindowQuery("s", searchTerms);
    } else {
      removeWindowQuery("s");
    }
  }

  const hasFilters =
    categoryFilterValues.length > 0 ||
    dateFilterValue !== DATE_FILTER_DEFAULT ||
    sortFilterValue !== SORT_FILTER_DEFAULT
      ? true
      : false;

  return (
    <>
      <HelmetSEO
        lang={story.lang}
        page_path={pagePage}
        story_alternates={pageData ? pageData.alternates : []}
        seo_data={pageData ? pageData.seo : {}}
      />
      <Navbar story_language={story.lang} />
      <PageContentWrapper>
        <HeaderBannerPanel
          blok={{ margin_top: "default", margin_bottom: "default" }}
          header_banner_panel={story.content.ads.header_banner_panel}
        />
        <div className={`view with-padding ${styles.searchResultsPage}`}>
          <div className="page-content">
            <div className={styles.searchHeader}>
              <p className={styles.resultsFor}>
                <FormattedMessage id="page.search.header.results_for" />
              </p>
              <div className={styles.searchField}>
                <input
                  type="text"
                  disabled={isLoading}
                  value={searchTerms}
                  onKeyDown={handleInputKeyDown}
                  onChange={updateSearchTerms}
                  placeholder={intl.formatMessage({
                    id: "page.search.header.placeholder",
                  })}
                />
                <button disabled={isLoading} onClick={handleClickSearch}>
                  <SearchSVG />
                </button>
              </div>
            </div>
            <div className={styles.layout}>
              <main>
                <div
                  className={`${styles.filterBar} ${
                    hasFilters ? styles.filterBarSmallMargin : ""
                  }`}
                >
                  <div>
                    <div
                      className={`limit-visibility ${styles.dropdown} ${
                        dateFilterVisible ? styles.dropdownVisible : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          toggleFilter(FILTERS.DATE);
                        }}
                      >
                        <FormattedMessage id="page.search.filter.date.label" />
                        <TriangleDownSVG />
                      </button>
                      <div
                        className={styles.dropdownBackdrop}
                        onClick={() => {
                          toggleFilter(FILTERS.DATE);
                        }}
                      />
                      <div className={styles.dropdownBox}>
                        <Radio
                          uniqueId="filter-date-day"
                          name="date"
                          checked={dateFilterValue === DATE_FILTERS.DAY}
                          onChange={() => {
                            updateDateFilter(DATE_FILTERS.DAY);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.date.today",
                          })}
                        />
                        <Radio
                          uniqueId="filter-date-week"
                          name="date"
                          checked={dateFilterValue === DATE_FILTERS.WEEK}
                          onChange={() => {
                            updateDateFilter(DATE_FILTERS.WEEK);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.date.week",
                          })}
                        />
                        <Radio
                          uniqueId="filter-date-month"
                          name="date"
                          checked={dateFilterValue === DATE_FILTERS.MONTH}
                          onChange={() => {
                            updateDateFilter(DATE_FILTERS.MONTH);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.date.month",
                          })}
                        />
                        <Radio
                          uniqueId="filter-date-year"
                          name="date"
                          checked={dateFilterValue === DATE_FILTERS.YEAR}
                          onChange={() => {
                            updateDateFilter(DATE_FILTERS.YEAR);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.date.year",
                          })}
                        />
                        <Radio
                          uniqueId="filter-date-all"
                          name="date"
                          checked={dateFilterValue === DATE_FILTERS.ALL}
                          onChange={() => {
                            updateDateFilter(DATE_FILTERS.ALL);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.date.all",
                          })}
                        />
                      </div>
                    </div>
                    <div
                      className={`limit-visibility ${styles.dropdown} ${
                        categoryFilterVisible ? styles.dropdownVisible : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          toggleFilter(FILTERS.CATEGORY);
                        }}
                      >
                        <FormattedMessage id="page.search.filter.category.label" />
                        <TriangleDownSVG />
                      </button>
                      <div
                        className={styles.dropdownBackdrop}
                        onClick={() => {
                          toggleFilter(FILTERS.CATEGORY);
                        }}
                      />
                      <div className={styles.dropdownBox}>
                        {categoriesLoading ? (
                          <div className="loader centered" />
                        ) : null}
                        {categoryFilterOptions.map((category) => (
                          <Checkbox
                            key={`filter-category-${category.key}`}
                            uniqueId={`filter-category-${category.key}`}
                            checked={
                              categoryFilterValues.indexOf(category.value) !==
                              -1
                            }
                            onChange={() => {
                              updateCategoryFilter(category.value);
                            }}
                            label={category.label}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      className={`limit-visibility ${styles.dropdown} ${
                        sortFilterVisible ? styles.dropdownVisible : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          toggleFilter(FILTERS.SORT);
                        }}
                      >
                        <FormattedMessage id="page.search.filter.sort.label" />
                        <TriangleDownSVG />
                      </button>
                      <div
                        className={styles.dropdownBackdrop}
                        onClick={() => {
                          toggleFilter(FILTERS.SORT);
                        }}
                      />
                      <div
                        className={`${styles.dropdownBox} ${styles.dropdownBoxRight}`}
                      >
                        <Radio
                          uniqueId="filter-sort-relevancy"
                          name="sort"
                          checked={sortFilterValue === SORT_FILTERS.RELEVANCY}
                          onChange={() => {
                            updateSortFilter(SORT_FILTERS.RELEVANCY);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.sort.relevancy",
                          })}
                        />
                        <Radio
                          uniqueId="filter-sort-newest"
                          name="sort"
                          checked={sortFilterValue === SORT_FILTERS.NEWEST}
                          onChange={() => {
                            updateSortFilter(SORT_FILTERS.NEWEST);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.sort.newest",
                          })}
                        />
                        <Radio
                          uniqueId="filter-sort-oldest"
                          name="sort"
                          checked={sortFilterValue === SORT_FILTERS.OLDEST}
                          onChange={() => {
                            updateSortFilter(SORT_FILTERS.OLDEST);
                          }}
                          label={intl.formatMessage({
                            id: "page.search.filter.sort.oldest",
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Filter Tags */}
                {hasFilters ? (
                  <div className={styles.categoryFilterTagList}>
                    {dateFilterValue !== DATE_FILTER_DEFAULT ? (
                      <span key={dateFilterValue}>
                        <label>
                          {GetDateFilterLabelFromKey(dateFilterValue)}
                        </label>
                        <button
                          onClick={() => {
                            updateDateFilter(DATE_FILTER_DEFAULT);
                          }}
                        >
                          <TimesOffCenterSVG />
                        </button>
                      </span>
                    ) : null}
                    {sortFilterValue !== SORT_FILTER_DEFAULT ? (
                      <span key={sortFilterValue}>
                        <label>
                          {GetSortFilterLabelFromKey(sortFilterValue)}
                        </label>
                        <button
                          onClick={() => {
                            updateSortFilter(SORT_FILTER_DEFAULT);
                          }}
                        >
                          <TimesOffCenterSVG />
                        </button>
                      </span>
                    ) : null}
                    {categoryFilterValues.map((value) => {
                      const category = categoryFilterOptions.find((c) => {
                        return c.value === value;
                      });
                      if (!category) {
                        return null;
                      }
                      return (
                        <span key={category.key}>
                          <label>{category.label}</label>
                          <button
                            onClick={() => {
                              removeCategoryFilter(value);
                            }}
                          >
                            <TimesOffCenterSVG />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {/* RESULTS */}
                <div className={styles.resultsWrapper}>
                  {isLoading ? <div className="loader centered" /> : null}
                  {currentArticles.map((r, idx) => (
                    <Fragment key={r.uuid}>
                      {idx === FEATURED ? (
                        <div
                          key={`banner_${idx}`}
                          className={styles.lowerBanner}
                        >
                          <LowerBannerPanel
                            blok={{
                              margin_top: "default",
                              margin_bottom: "default",
                            }}
                            lower_banner_panel={
                              story.content.ads.lower_banner_panel
                            }
                          />
                        </div>
                      ) : null}
                      {idx !== FEATURED && idx > 0 ? (
                        <hr key={`hr_${idx}`} />
                      ) : null}
                      <SearchCard
                        key={r.uuid}
                        slug={r.slug}
                        title={r.content.headline}
                        lead={r.content.lead}
                        image={
                          r.content.thumbnail && r.content.thumbnail.filename
                            ? r.content.thumbnail
                            : globalSettings.thumbnail_default_image
                        }
                        use_legacy_thumbnail_background={
                          r.content.legacy_thumbnail_background
                        }
                        date={r.content.created_at}
                        article_type={r.content.article_type}
                        article_category={r.content.category}
                        authors={r.content.authors}
                        enable_comments={r.content.has_comments !== "no"}
                        enable_social_sharing={
                          r.content.has_social_sharing !== "no"
                        }
                      />
                    </Fragment>
                  ))}
                  {totalArticles <= 0 && !isLoading ? (
                    <p className={styles.noResults}>
                      <FormattedMessage id="page.search.no_results" />
                    </p>
                  ) : null}
                  {totalArticles > PER_PAGE ? (
                    <Pagination
                      currentPage={currentPage}
                      totalCount={totalArticles}
                      pageSize={PER_PAGE}
                      onPageChange={handlePageChange}
                    />
                  ) : null}
                </div>
              </main>
              <StickyBottomAside>
                <div className={styles.asideContent}>{asideComponentList}</div>
              </StickyBottomAside>
            </div>
          </div>
        </div>
      </PageContentWrapper>
      <Footer />
    </>
  );
}

export default SearchResultsTemplate;
